/* ==========================================================================
   Assay - theme behaviour
   Custom elements only. No framework, no build step.
   ========================================================================== */

(function () {
  'use strict';

  const strings = (() => {
    const el = document.getElementById('theme-strings');
    try {
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  })();

  const money = (cents, format) => {
    const amount = (cents / 100).toFixed(2);
    return (format || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  /* ------------------------------------------------------------------------
     Cart store - one source of truth, everything subscribes
     ------------------------------------------------------------------------ */

  const Cart = {
    listeners: new Set(),

    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    },

    emit(state) {
      this.listeners.forEach((fn) => fn(state));
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(Object.assign({ sections: 'cart-drawer' }, body)),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || strings.cartError);
      return data;
    },

    async add(id, quantity, properties) {
      const payload = { items: [{ id: Number(id), quantity: quantity || 1 }] };
      if (properties) payload.items[0].properties = properties;
      const data = await this.request('/cart/add.js', payload);
      await this.refresh(data.sections);
      return data;
    },

    async change(line, quantity) {
      const data = await this.request('/cart/change.js', { line: line, quantity: quantity });
      await this.refresh(data.sections);
      return data;
    },

    async refresh(sections) {
      let html = sections && sections['cart-drawer'];

      if (!html) {
        const res = await fetch('/?section_id=cart-drawer');
        html = await res.text();
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer]');
      const current = document.querySelector('[data-cart-drawer]');

      if (fresh && current) {
        const wasOpen = current.dataset.open === 'true';
        current.innerHTML = fresh.innerHTML;
        current.dataset.count = fresh.dataset.count;
        if (wasOpen) current.dataset.open = 'true';
      }

      const count = current ? Number(current.dataset.count || 0) : 0;
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = count;
      });

      this.emit({ count: count });
    },
  };

  window.themeCart = Cart;

  /* ------------------------------------------------------------------------
     Cart drawer
     ------------------------------------------------------------------------ */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dataset.open === 'true') this.close();
      });

      document.addEventListener('cart:open', () => this.open());
    }

    onClick(event) {
      const close = event.target.closest('[data-drawer-close]');
      if (close || event.target.hasAttribute('data-drawer-scrim')) {
        event.preventDefault();
        this.close();
        return;
      }

      const step = event.target.closest('[data-qty-step]');
      if (step) {
        event.preventDefault();
        this.step(step);
        return;
      }

      const remove = event.target.closest('[data-line-remove]');
      if (remove) {
        event.preventDefault();
        this.update(remove.dataset.line, 0);
      }
    }

    step(button) {
      const wrapper = button.closest('[data-line]');
      const input = wrapper.querySelector('input');
      const delta = button.dataset.qtyStep === 'up' ? 1 : -1;
      const next = Math.max(0, Number(input.value) + delta);
      input.value = next;
      this.update(wrapper.dataset.line, next);
    }

    async update(line, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        await Cart.change(Number(line), quantity);
      } catch (error) {
        console.error(error);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    open() {
      this.dataset.open = 'true';
      document.body.style.overflow = 'hidden';
      const focusable = this.querySelector('[data-drawer-close]');
      if (focusable) focusable.focus();
    }

    close() {
      this.dataset.open = 'false';
      document.body.style.overflow = '';
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  /* ------------------------------------------------------------------------
     Add to cart form
     ------------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add]');
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      if (this.dataset.cartType !== 'drawer') return;

      event.preventDefault();
      const id = this.form.querySelector('[name="id"]').value;
      const qtyField = this.form.querySelector('[name="quantity"]');
      const quantity = qtyField ? Number(qtyField.value) : 1;

      const label = this.button.textContent;
      this.button.disabled = true;
      this.button.textContent = strings.addingToCart || label;

      try {
        await Cart.add(id, quantity);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (error) {
        this.renderError(error.message);
      } finally {
        this.button.disabled = false;
        this.button.textContent = label;
      }
    }

    renderError(message) {
      let node = this.querySelector('[data-form-error]');
      if (!node) {
        node = document.createElement('p');
        node.setAttribute('data-form-error', '');
        node.className = 'field__error hallmark';
        this.appendChild(node);
      }
      node.textContent = message;
    }
  }

  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.moneyFormat = this.dataset.moneyFormat;
      this.addEventListener('change', this.onChange.bind(this));
    }

    get selected() {
      return Array.from(this.querySelectorAll('input:checked')).map((input) => input.value);
    }

    onChange() {
      const options = this.selected;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, index) => value === options[index])
      );

      this.querySelectorAll('[data-option-value]').forEach((node) => {
        const index = Number(node.dataset.optionIndex);
        node.textContent = options[index];
      });

      this.update(variant);
    }

    update(variant) {
      const root = this.closest('[data-product-root]') || document;
      const idField = root.querySelector('[name="id"]');
      const button = root.querySelector('[data-add]');
      const price = root.querySelector('[data-price]');
      const stock = root.querySelector('[data-stock]');
      const sku = root.querySelector('[data-sku]');

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.textContent = strings.unavailable || 'Unavailable';
        }
        return;
      }

      if (idField) idField.value = variant.id;

      if (price) {
        price.innerHTML = variant.compare_at_price > variant.price
          ? '<s>' + money(variant.compare_at_price, this.moneyFormat) + '</s>' +
            money(variant.price, this.moneyFormat)
          : money(variant.price, this.moneyFormat);
      }

      if (sku) sku.textContent = variant.sku || '';

      if (stock) {
        stock.classList.toggle('stock--out', !variant.available);
        stock.textContent = variant.available
          ? stock.dataset.inStock
          : stock.dataset.outOfStock;
      }

      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available
          ? strings.addToCart || 'Add to cart'
          : strings.soldOut || 'Sold out';
      }

      this.swapMedia(variant);

      if (variant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    swapMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('product-gallery');
      if (gallery && gallery.selectByMediaId) {
        gallery.selectByMediaId(variant.featured_media.id);
      }
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------------
     Carousel
     Scroll-snap does the scrolling; this only drives the arrows and hides
     them when everything already fits.
     ------------------------------------------------------------------------ */

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');

      if (this.prev) this.prev.addEventListener('click', () => this.advance(-1));
      if (this.next) this.next.addEventListener('click', () => this.advance(1));

      this.sync = this.sync.bind(this);
      this.track.addEventListener('scroll', this.sync, { passive: true });
      window.addEventListener('resize', this.sync);

      this.sync();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.sync);
    }

    step() {
      const first = this.track.firstElementChild;
      if (!first) return this.track.clientWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    advance(direction) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: this.step() * direction,
        behavior: reduced ? 'auto' : 'smooth',
      });
    }

    sync() {
      const overflow = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= overflow - 2;
      if (overflow <= 2) {
        this.setAttribute('data-static', '');
      } else {
        this.removeAttribute('data-static');
      }
    }
  }

  customElements.define('product-carousel', ProductCarousel);

  /* ------------------------------------------------------------------------
     Hero carousel
     Scroll-snap does the swiping. This drives the arrows and indicators,
     wraps from the last slide to the first, keeps off-screen slides out of
     the tab order, and runs the optional auto-rotate.
     ------------------------------------------------------------------------ */

  /* Hero background videos play muted on a loop. Anyone who has asked for
     reduced motion gets the still first frame instead. */
  const heroMotionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setHeroVideo = (video, playing) => {
    if (!video) return;
    if (playing && !heroMotionOff()) {
      const attempt = video.play();
      // Autoplay can be refused (low-power mode); the poster stays up.
      if (attempt && attempt.catch) attempt.catch(() => {});
    } else {
      video.pause();
    }
  };

  const stillHeroVideos = () => {
    if (!heroMotionOff()) return;
    document.querySelectorAll('.hero__video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  };

  document.addEventListener('DOMContentLoaded', stillHeroVideos);
  document.addEventListener('shopify:section:load', stillHeroVideos);

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.userGo(this.index - 1);
        if (event.key === 'ArrowRight') this.userGo(this.index + 1);
      });

      // Any swipe or trackpad scroll counts as taking over from auto-rotate.
      this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
      this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
      this.track.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.stop();
      }, { passive: true });

      // Later slides load lazily, but should be ready before anyone swipes.
      const warm = () => {
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      };
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });

      this.update();
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => this.place());
        this.observer.observe(this.track);
      }

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.observer) this.observer.disconnect();
    }

    onClick(event) {
      const button = event.target.closest('[data-hero-prev], [data-hero-next], [data-hero-dot]');
      if (!button) return;
      event.preventDefault();
      if (button.hasAttribute('data-hero-prev')) this.userGo(this.index - 1);
      else if (button.hasAttribute('data-hero-next')) this.userGo(this.index + 1);
      else this.userGo(Number(button.dataset.heroDot));
    }

    onScroll() {
      const width = this.track.clientWidth;
      if (!width) return;
      const at = Math.round(this.track.scrollLeft / width);
      if (at !== this.index && this.slides[at]) {
        this.index = at;
        this.update();
      }
    }

    onResize() {
      // Keep the current slide in place when the width changes under it.
      this.go(this.index, 'auto');
    }

    // In the theme editor, selecting a slide block brings it into view.
    onBlockSelect(event) {
      const at = this.slides.indexOf(event.target);
      if (at < 0) return;
      this.stop();
      this.go(at, 'auto');
    }

    userGo(index) {
      this.stop();
      this.go(index);
    }

    go(index, behavior) {
      const count = this.slides.length;
      const at = (index + count) % count;
      this.track.scrollTo({
        left: at * this.track.clientWidth,
        behavior: this.reduced ? 'auto' : behavior || 'smooth',
      });
    }

    update() {
      this.slides.forEach((slide, at) => {
        const active = at === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        // Only the slide in view spends bandwidth and battery on its video.
        setHeroVideo(slide.querySelector('.hero__video'), active);
      });
      this.dots.forEach((dot, at) => {
        dot.setAttribute('aria-current', String(at === this.index));
      });
      this.place();
    }

    /* Pin the arrows and indicators to the active slide's image: the whole
       frame in the overlay layout, just the picture in split and stacked. */
    place() {
      const slide = this.slides[this.index];
      if (!slide) return;
      const media = slide.querySelector('.hero__media');
      const box = slide.querySelector('.hero__frame') || media;
      if (!media || !box) return;

      // Measured against the slide, not the screen: this runs mid-swipe,
      // while the slide is still partly off to one side. Every slide is the
      // carousel's full width, so its own coordinates hold once it settles.
      const own = slide.getBoundingClientRect();
      const frame = box.getBoundingClientRect();
      const image = media.getBoundingClientRect();

      this.style.setProperty('--hero-arrow-l', frame.left - own.left + 'px');
      this.style.setProperty('--hero-arrow-r', own.right - frame.right + 'px');
      this.style.setProperty('--hero-arrow-y', image.top - own.top + image.height / 2 + 'px');
      this.style.setProperty('--hero-dots-x', image.left - own.left + image.width / 2 + 'px');
      this.style.setProperty('--hero-dots-y', image.bottom - own.top - 12 + 'px');
    }

    start() {
      if (this.dataset.autoplay !== 'true' || this.reduced) return;
      if (window.Shopify && window.Shopify.designMode) return;

      const seconds = Number(this.dataset.speed) || 6;
      this.paused = false;
      this.pause = () => { this.paused = true; };
      this.resume = () => { this.paused = false; };
      this.addEventListener('pointerenter', this.pause);
      this.addEventListener('pointerleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);

      this.timer = window.setInterval(() => {
        if (!this.paused && !document.hidden) this.go(this.index + 1);
      }, seconds * 1000);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('hero-carousel', HeroCarousel);

  /* ------------------------------------------------------------------------
     Product gallery
     One image at full size; the thumbnails swap what it shows.
     ------------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.dialog = this.querySelector('[data-lightbox]');
      this.stage = this.querySelector('[data-lightbox-img]');
      this.counter = this.querySelector('[data-lightbox-count]');

      // Images only: videos have no place in the full-size image viewer.
      const source = this.querySelector('[data-gallery-media]');
      try {
        this.media = source ? JSON.parse(source.textContent) : [];
      } catch (error) {
        this.media = [];
      }
      this.index = 0;

      this.addEventListener('click', this.onClick.bind(this));

      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.fine) this.initLoupe();

      // Matches the breakpoint where the stylesheet turns the stage into a
      // swipeable row.
      this.swipe = window.matchMedia('(max-width: 899px)');
      this.track = this.querySelector('.gallery__stage');
      if (this.track && this.items.length > 1) {
        this.track.addEventListener('scroll', this.onSwipe.bind(this), { passive: true });
        this.swipe.addEventListener('change', () => this.scrollToCurrent('auto'));
        // A variant can open the page on an image other than the first.
        requestAnimationFrame(() => this.scrollToCurrent('auto'));
      }

      if (this.dialog) {
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('close', () => {
          document.body.style.overflow = '';
        });
      }

      document.addEventListener('keydown', (event) => {
        if (!this.dialog || !this.dialog.open) return;
        if (event.key === 'ArrowRight') this.step(1);
        if (event.key === 'ArrowLeft') this.step(-1);
      });
    }

    onClick(event) {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) {
        event.preventDefault();
        this.select(thumb.dataset.mediaId);
        return;
      }

      const opener = event.target.closest('[data-gallery-open]');
      if (opener) {
        event.preventDefault();
        // Pointer devices inspect with the loupe; a keyboard activation
        // reports detail 0 and still opens the viewer.
        if (!this.fine || event.detail === 0) this.open(opener.dataset.mediaId);
        return;
      }

      if (event.target.closest('[data-lightbox-close]')) {
        event.preventDefault();
        this.close();
      } else if (event.target.closest('[data-lightbox-prev]')) {
        event.preventDefault();
        this.step(-1);
      } else if (event.target.closest('[data-lightbox-next]')) {
        event.preventDefault();
        this.step(1);
      }
    }

    /* Leaving a video playing behind a hidden panel is the classic version of
       this bug: audio keeps going with nothing on screen. */
    stopMedia(item) {
      const video = item.querySelector('video');
      if (video && !video.paused) video.pause();

      // A hosted embed cannot be paused from here, so reload it instead.
      const frame = item.querySelector('iframe');
      if (frame && frame.src) frame.src = frame.src;
    }

    /* Swiping picks whichever item is in view, so the thumbnails, the
       viewer's starting image and video pausing all follow along. */
    onSwipe() {
      if (!this.swipe.matches) return;
      const width = this.track.clientWidth;
      if (!width) return;
      const item = this.items[Math.round(this.track.scrollLeft / width)];
      if (item && item.hidden) this.select(item.dataset.mediaId, true);
    }

    scrollToCurrent(behavior) {
      if (!this.track || !this.swipe.matches) return;
      const at = this.items.findIndex((item) => !item.hidden);
      if (at < 0) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: at * this.track.clientWidth,
        behavior: reduced ? 'auto' : behavior,
      });
    }

    select(id, fromSwipe) {
      const wanted = String(id);

      this.items.forEach((item) => {
        const active = item.dataset.mediaId === wanted;
        if (!active && !item.hidden) this.stopMedia(item);
        item.hidden = !active;
      });

      this.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        button.setAttribute('aria-current', String(button.dataset.mediaId === wanted));
      });

      const at = this.media.findIndex((entry) => String(entry.id) === wanted);
      if (at > -1) this.index = at;

      if (!fromSwipe) this.scrollToCurrent('smooth');
    }

    selectByMediaId(id) {
      this.select(id);
    }

    initLoupe() {
      this.querySelectorAll('[data-gallery-open]').forEach((frame) => {
        const lens = frame.querySelector('[data-lens]');
        if (!lens) return;

        const zoom = Number(this.dataset.zoom || 2.4);

        frame.addEventListener('pointerenter', () => {
          lens.style.backgroundImage = 'url("' + frame.dataset.zoomSrc + '")';
          frame.dataset.active = 'true';
        });

        frame.addEventListener('pointerleave', () => {
          frame.dataset.active = 'false';
        });

        const img = frame.querySelector('img');

        frame.addEventListener('pointermove', (event) => {
          const rect = frame.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;

          // With a cropped gallery the image overflows the frame on one axis,
          // so the magnified copy has to be sized and offset to match.
          let width = rect.width;
          let height = rect.height;
          let offsetX = 0;
          let offsetY = 0;
          if (img && img.naturalWidth && img.naturalHeight) {
            const ratio = img.naturalWidth / img.naturalHeight;
            if (ratio > width / height) {
              width = height * ratio;
              offsetX = (rect.width - width) / 2;
            } else {
              height = width / ratio;
              offsetY = (rect.height - height) / 2;
            }
          }

          lens.style.transform =
            'translate(' + x + 'px, ' + y + 'px) translate(-50%, -50%)';
          lens.style.backgroundSize =
            width * zoom + 'px ' + height * zoom + 'px';
          lens.style.backgroundPosition =
            -((x - offsetX) * zoom - lens.offsetWidth / 2) + 'px ' +
            -((y - offsetY) * zoom - lens.offsetHeight / 2) + 'px';
        });
      });
    }

    open(id) {
      if (!this.dialog) return;

      if (id !== undefined) {
        const at = this.media.findIndex((entry) => String(entry.id) === String(id));
        if (at > -1) this.index = at;
      }

      this.render();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.style.overflow = '';
    }

    step(direction) {
      if (!this.media.length) return;
      this.index = (this.index + direction + this.media.length) % this.media.length;
      this.render();
    }

    render() {
      const item = this.media[this.index];
      if (!item || !this.stage) return;
      this.stage.src = item.src;
      this.stage.alt = item.alt || '';
      if (item.w) this.stage.width = item.w;
      if (item.h) this.stage.height = item.h;
      if (this.counter) {
        this.counter.textContent = (this.index + 1) + ' / ' + this.media.length;
      }
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------------
     Popup
     ------------------------------------------------------------------------ */

  class StorePopup extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key;
      this.shown = false;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-popup-close]') ||
            event.target.hasAttribute('data-popup-scrim')) {
          event.preventDefault();
          this.dismiss();
          return;
        }

        // Using the call to action counts as engaging with the popup. Recorded
        // without preventDefault, so the link still navigates.
        if (event.target.closest('[data-popup-action]') && this.dismissOnAction()) {
          this.remember();
        }
      });

      // Catches the signup form submitted by keyboard as well as by button.
      this.addEventListener('submit', () => {
        if (this.dismissOnAction()) this.remember();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dataset.open === 'true') this.dismiss();
      });

      // In the theme editor it must always be visible, otherwise it cannot be
      // styled. Frequency rules are ignored there.
      if (window.Shopify && window.Shopify.designMode) {
        this.show();
        return;
      }

      // Present in the DOM only so the editor can show it; not live yet.
      if (this.dataset.enabled !== 'true') return;

      if (this.seen()) return;
      this.arm();
    }

    /* localStorage throws in some privacy modes; a popup is never worth an
       exception that stops the rest of the script. */
    read() {
      try {
        return window.localStorage.getItem(this.key);
      } catch (error) {
        return null;
      }
    }

    write(value) {
      try {
        window.localStorage.setItem(this.key, value);
      } catch (error) {
        /* nothing to do */
      }
    }

    seen() {
      const mode = this.dataset.frequency;
      if (mode === 'always') return false;

      if (mode === 'session') {
        try {
          return window.sessionStorage.getItem(this.key) === '1';
        } catch (error) {
          return false;
        }
      }

      const stamp = Number(this.read());
      if (!stamp) return false;
      const days = Number(this.dataset.days || 14);
      return Date.now() - stamp < days * 86400000;
    }

    arm() {
      const trigger = this.dataset.trigger;

      if (trigger === 'scroll') {
        const depth = Number(this.dataset.scroll || 40) / 100;
        const onScroll = () => {
          const reach = window.scrollY /
            Math.max(1, document.body.scrollHeight - window.innerHeight);
          if (reach >= depth) {
            window.removeEventListener('scroll', onScroll);
            this.show();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return;
      }

      if (trigger === 'exit') {
        // Exit intent has no touch equivalent, so fall back to a delay there.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          const onLeave = (event) => {
            if (event.clientY > 0) return;
            document.removeEventListener('mouseout', onLeave);
            this.show();
          };
          document.addEventListener('mouseout', onLeave);
        } else {
          window.setTimeout(() => this.show(), 12000);
        }
        return;
      }

      window.setTimeout(() => this.show(), Number(this.dataset.delay || 6) * 1000);
    }

    show() {
      if (this.shown) return;
      this.shown = true;
      this.hidden = false;
      // Next frame, so the transition has a starting state to animate from.
      window.requestAnimationFrame(() => {
        this.dataset.open = 'true';
      });
    }

    dismissOnAction() {
      return this.dataset.dismissOnAction === 'true';
    }

    /* Marks this visitor as having seen it, without hiding anything. Split out
       so a click on the call to action can record it while the browser is
       already navigating away. */
    remember() {
      if (this.dataset.frequency === 'session') {
        try {
          window.sessionStorage.setItem(this.key, '1');
        } catch (error) {
          /* nothing to do */
        }
        return;
      }
      this.write(String(Date.now()));
    }

    dismiss() {
      this.dataset.open = 'false';
      window.setTimeout(() => {
        this.hidden = true;
      }, 400);
      this.remember();
    }
  }

  customElements.define('store-popup', StorePopup);

  /* ------------------------------------------------------------------------
     Mobile menu
     ------------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-menu-toggle]');
    if (!toggle) return;

    event.preventDefault();
    const menu = document.querySelector('[data-menu]');
    if (!menu) return;

    const open = menu.dataset.open !== 'true';
    menu.dataset.open = String(open);
    document.body.style.overflow = open ? 'hidden' : '';
    toggle.setAttribute('aria-expanded', String(open));
  });

  /* ------------------------------------------------------------------------
     Cart trigger
     ------------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;

    event.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:open'));
  });

  /* ------------------------------------------------------------------------
     Scroll reveal - one orchestrated entrance, staggered by position
     ------------------------------------------------------------------------ */

  const reveal = () => {
    const items = document.querySelectorAll('.reveal:not([data-shown])');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.setAttribute('data-shown', 'true'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (!entry.isIntersecting) return;
          entry.target.style.setProperty('--reveal-delay', index * 70 + 'ms');
          entry.target.setAttribute('data-shown', 'true');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach((el) => observer.observe(el));
  };

  document.addEventListener('DOMContentLoaded', reveal);
  document.addEventListener('shopify:section:load', reveal);

  /* ------------------------------------------------------------------------
     Collection sort
     ------------------------------------------------------------------------ */

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort]');
    if (!sort) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });
})();
